import { ChatMessages, type Icons, type RichText } from "@storytell/cp-client";

export const END_TURN_OPERATION_ID = "operation_00000000000000000000"; // Special operation ID for end of conversation

export const isEndTurnOperationId = (operationId: string): boolean => operationId === END_TURN_OPERATION_ID;

// UI Renderer types
export enum BlockRenderer {
  USER_FACING_MESSAGE = "UserFacingMessage",
  BLOCK_PREVIEW = "BlockPreview",
  ARTIFACT_VIEWER = "ArtifactViewer",
  TASK_CARD = "TaskCard",
  PROMPT_ACK = "PromptAck",
  INTERSTITIAL_DATA = "InterstitialData",
  INTERSTITIAL_USER_FACING = "InterstitialUserFacing",
  INTERSTITIAL_CATCH = "InterstitialCatch",
  INTERSTITIAL = "Interstitial",
  TURN_COMPLETE = "TurnComplete",
  UNKNOWN = "Unknown", // Fallback for unknown types
}

const renderMapping: Record<ChatMessages.MessageType, BlockRenderer> = {
  [ChatMessages.MessageType.UNSPECIFIED]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.PROMPT_ACK]: BlockRenderer.PROMPT_ACK,
  [ChatMessages.MessageType.INTERSTITIAL]: BlockRenderer.INTERSTITIAL,
  [ChatMessages.MessageType.INTERSTITIAL_DATA]: BlockRenderer.INTERSTITIAL_DATA,
  [ChatMessages.MessageType.INTERSTITIAL_USER_FACING]: BlockRenderer.INTERSTITIAL_USER_FACING,
  [ChatMessages.MessageType.INTERSTITIAL_CATCH]: BlockRenderer.INTERSTITIAL_CATCH,
  [ChatMessages.MessageType.SCOPED]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.REFERENCED]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.RATE_FEEDBACK]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.SYSTEM_STATUS]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.MEMORY_UPDATE]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.TASK_CREATE]: BlockRenderer.TASK_CARD,
  [ChatMessages.MessageType.TASK_UPDATE]: BlockRenderer.TASK_CARD,
  [ChatMessages.MessageType.OPERATION_STATUS]: BlockRenderer.TASK_CARD,
  [ChatMessages.MessageType.CONTENT]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_EXPLANATION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_CITATION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_SUMMARY]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_DISCLAIMER]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_ALTERNATIVE]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_ELABORATION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_PREREQUISITE]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_CLARIFICATION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_NEXT_STEPS]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_ERROR_CORRECTION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_WALKTHROUGH]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_TECHNICAL_DETAILS]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_HISTORICAL_CONTEXT]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_COMPARISON]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_EXAMPLE]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.DATA_VISUALIZATION]: BlockRenderer.BLOCK_PREVIEW,
  [ChatMessages.MessageType.SUGGESTED_ACTION]: BlockRenderer.BLOCK_PREVIEW,
  [ChatMessages.MessageType.CLARIFICATION_REQUEST]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.ARTIFACT_CREATION]: BlockRenderer.ARTIFACT_VIEWER,
  [ChatMessages.MessageType.ARTIFACT_REVISION]: BlockRenderer.ARTIFACT_VIEWER,
};

export function determinePreferredRenderer(block: { messageType?: ChatMessages.MessageType }): BlockRenderer {
  if (block.messageType && renderMapping[block.messageType]) {
    return renderMapping[block.messageType];
  }
  return BlockRenderer.UNKNOWN;
}

// Task list interface for UI components
export interface TaskList {
  tasks: Operation[];
  totalCount: number;
  completedCount: number;
  failedCount: number;
  operationId: string;
}

// Core data structures for improved streaming implementation
export interface Message {
  id: string; // "message_d18rj0g22d23ej1oroeg"
  index: number; // 1, 2, 3
  role: ChatMessages.Role;

  // Start data - available immediately
  messageType: ChatMessages.MessageType; // MESSAGE_TYPE_INTERSTITIAL
  feature: string; // "FEATURE_MODEL_STORYTELL_AGENT_V3"
  title?: string; // "Parsing...", "Working through the details..."
  promptText?: string; // "Analyze the market for Free Ad Supported..."
  icon?: Icons.Icon | string; // "ICON_INVESTIGATION"

  // Rich text version sent at completion
  richText?: RichText.RichText; // Rich text version sent at completion

  // Nested related entities (no flat access - memory efficient)
  operations: Operation[]; // Operations that belong to this message

  // Final metrics when message completes (breakdown by feature)
  featureMetrics: Record<string, FeatureMetrics>; // "anthropic-claude-3-7-sonnet" -> final metrics

  // Single aggregated metrics during streaming (for simple real-time feedback)
  aggregatedMetrics: AggregatedMetrics; // total across all features

  // State
  startedAt: Date;
  completedAt?: Date;
  isComplete: boolean;
}

export interface Block {
  id: string; // "block_d18rj1022d23ej1orogg"
  role: ChatMessages.Role;
  activity?: string; // "Initial Review", "Artifact Generator"

  // Start data
  messageType: ChatMessages.MessageType; // MESSAGE_TYPE_INTERSTITIAL
  feature: string; // "FEATURE_MODEL_ANTHROPIC_CLAUDE_37_SONNET"
  title?: string; // "Working through the details..."
  icon?: Icons.Icon | string;

  // Rich text version sent at block completion
  richText?: RichText.RichText; // Rich text version sent at block completion

  // Search results when available
  searchResults?: ChatMessages.SearchResult;
  // Web search results when available
  webSearchResults?: ChatMessages.WebSearchResult;

  // Token/byte accounting (accumulated from deltas)
  // Note: Real-time metrics are handled via events, these are final totals
  ingressTokens: number;
  egressTokens: number;
  ingressBytes: number;
  egressBytes: number;

  // UI rendering hint (assigned by streamer)
  preferredRenderer: BlockRenderer;

  // State
  startedAt: Date;
  completedAt?: Date;
  isComplete: boolean;
}

export interface Operation {
  id: string; // "operation_d18rj0g22d23ej1orofg"
  activity: string; // "triage_v1", "intent_v1", "strategy_v1"
  name: string; // "Initial Review", "Intent Analysis", "Strategy Planning"
  status: ChatMessages.OperationStatus; // STARTING, RUNNING, COMPLETED
  progressMessage?: string; // "Processing your message to determine..."

  // Special operation types
  isPromptOperation?: boolean; // true for operations that contain PROMPT_ACK blocks

  // Task-like properties (operations can be tasks)
  taskId?: string; // "triage_analysis" - when first emitted as task
  taskName?: string; // "Analyzing your request"
  taskStatus?: ChatMessages.TaskStatus; // PLANNED, RUNNING, COMPLETED
  progressPercent?: number; // 0-100
  currentActivity?: string; // "Processing your message..."
  dependencies: string[]; // ["triage_analysis"]
  metadata: Record<string, string>; // task-specific metadata
  errorMessage?: string;
  icon?: Icons.Icon | string; // "ICON_INVESTIGATION"
  description?: string; // Task description from durationInfo or metadata

  // Duration estimates (from task duration info)
  estimatedDurationMs?: number;
  minDurationMs?: number;
  maxDurationMs?: number;
  durationCategory?: string; // "workflow", "search", "analysis"
  durationConfidence?: string; // "high", "medium", "low"
  isSynchronous?: boolean;

  // Nested blocks (no separate flat storage)
  blocks: Block[]; // All blocks that belong to this operation

  // Timing
  startedAt: Date;
  completedAt?: Date;
  duration?: number; // milliseconds when complete
}

export interface FeatureTokens {
  ingressCredits?: number;
  egressCredits?: number;
  totalCredits?: number;
}

export interface FeatureMetrics {
  ingressTokens: number; // 24204
  egressTokens: number; // 7618
  ingressBytes: number; // 108405
  egressBytes: number; // 36292
  featureTokens?: FeatureTokens;
}

// Single aggregated metrics across all features (for simple real-time display)
export interface AggregatedMetrics {
  ingressTokens: number;
  egressTokens: number;
  ingressBytes: number;
  egressBytes: number;
  // Credit information from featureTokens
  totalCredits?: number;
  ingressCredits?: number;
  egressCredits?: number;
  // User's current credit balance from creditStatement
  balance?: number;
  // Last update timestamp for UI refresh logic
  lastUpdated: Date;
}

// Conversation-level structure (SolidJS reactive store friendly)
export interface ConversationState {
  messages: Message[]; // All messages in order - reactive array

  // Current state pointers (just IDs to avoid reference issues)
  currentMessageId?: string;
  currentOperationId?: string;
  isComplete: boolean;

  // Final aggregated metrics across all messages
  totalMetrics: Record<string, FeatureMetrics>;

  // Real-time aggregated metrics across all messages (for simple display)
  totalAggregatedMetrics: AggregatedMetrics;
}

// Helper functions for working with the reactive store
export function findMessageById(state: ConversationState, messageId: string): Message | undefined {
  return state.messages.find((m) => m.id === messageId);
}

export function findOperationById(state: ConversationState, operationId: string): Operation | undefined {
  for (const message of state.messages) {
    const operation = message.operations.find((op) => op.id === operationId);
    if (operation) return operation;
  }
  return undefined;
}

export function findBlockById(state: ConversationState, blockId: string): Block | undefined {
  for (const message of state.messages) {
    for (const operation of message.operations) {
      const block = operation.blocks.find((b) => b.id === blockId);
      if (block) return block;
    }
  }
  return undefined;
}

export function getCurrentMessage(state: ConversationState): Message | undefined {
  return state.currentMessageId ? findMessageById(state, state.currentMessageId) : undefined;
}

export function getCurrentOperation(state: ConversationState): Operation | undefined {
  return state.currentOperationId ? findOperationById(state, state.currentOperationId) : undefined;
}

// Factory functions for creating new entities (SolidJS store friendly)
export function createMessage(data: {
  id: string;
  index: number;
  role: ChatMessages.Role;
  messageType: ChatMessages.MessageType;
  feature: string;
  title?: string;
  promptText?: string;
  icon?: Icons.Icon | string;
}): Message {
  return {
    id: data.id,
    index: data.index,
    role: data.role,
    messageType: data.messageType,
    feature: data.feature,
    title: data.title,
    promptText: data.promptText,
    icon: data.icon,
    operations: [], // Start with empty operations array
    featureMetrics: {},
    aggregatedMetrics: {
      ingressTokens: 0,
      egressTokens: 0,
      ingressBytes: 0,
      egressBytes: 0,
      totalCredits: 0,
      ingressCredits: 0,
      egressCredits: 0,
      lastUpdated: new Date(),
    },
    startedAt: new Date(),
    isComplete: false,
  };
}

export function createOperation(data: {
  id: string;
  activity: string;
  name: string;
  status: ChatMessages.OperationStatus;
  progressMessage?: string;
  isPromptOperation?: boolean;
  taskId?: string;
  taskName?: string;
  taskStatus?: ChatMessages.TaskStatus;
  progressPercent?: number;
  currentActivity?: string;
  dependencies?: string[];
  metadata?: Record<string, string>;
  errorMessage?: string;
  icon?: Icons.Icon | string;
  description?: string;
  estimatedDurationMs?: number;
  minDurationMs?: number;
  maxDurationMs?: number;
  durationCategory?: string;
  durationConfidence?: string;
  isSynchronous?: boolean;
  startedAt?: Date;
  completedAt?: Date;
}): Operation {
  return {
    id: data.id,
    activity: data.activity,
    name: data.name,
    status: data.status,
    progressMessage: data.progressMessage,
    isPromptOperation: data.isPromptOperation,
    taskId: data.taskId,
    taskName: data.taskName,
    taskStatus: data.taskStatus,
    progressPercent: data.progressPercent,
    currentActivity: data.currentActivity,
    dependencies: data.dependencies || [],
    metadata: data.metadata || {},
    errorMessage: data.errorMessage,
    icon: data.icon,
    description: data.description,
    estimatedDurationMs: data.estimatedDurationMs,
    minDurationMs: data.minDurationMs,
    maxDurationMs: data.maxDurationMs,
    durationCategory: data.durationCategory,
    durationConfidence: data.durationConfidence,
    isSynchronous: data.isSynchronous,
    blocks: [], // Start with empty blocks array
    startedAt: data.startedAt || new Date(), // Use provided startedAt or fallback to current time
    completedAt: data.completedAt,
  };
}

export function createBlock(data: {
  id: string;
  role: ChatMessages.Role;
  activity?: string;
  messageType: ChatMessages.MessageType;
  feature: string;
  title?: string;
  icon?: Icons.Icon | string;
  startedAt?: Date;
}): Block {
  return {
    id: data.id,
    role: data.role,
    activity: data.activity,
    messageType: data.messageType,
    feature: data.feature,
    title: data.title,
    icon: data.icon,
    ingressTokens: 0,
    egressTokens: 0,
    ingressBytes: 0,
    egressBytes: 0,
    preferredRenderer: determinePreferredRenderer(data),
    startedAt: data.startedAt || new Date(), // Use provided startedAt or fallback to current time
    isComplete: false,
  };
}

// Utility function to accumulate metrics (simple aggregation)
export function accumulateAggregatedMetrics(
  currentMetrics: AggregatedMetrics,
  ingressTokens: number,
  egressTokens: number,
  ingressBytes: number,
  egressBytes: number,
  featureTokens?: FeatureTokens,
): AggregatedMetrics {
  return {
    ingressTokens: currentMetrics.ingressTokens + ingressTokens,
    egressTokens: currentMetrics.egressTokens + egressTokens,
    ingressBytes: currentMetrics.ingressBytes + ingressBytes,
    egressBytes: currentMetrics.egressBytes + egressBytes,
    totalCredits: featureTokens?.totalCredits
      ? (currentMetrics.totalCredits || 0) + featureTokens.totalCredits
      : currentMetrics.totalCredits,
    ingressCredits: featureTokens?.ingressCredits
      ? (currentMetrics.ingressCredits || 0) + featureTokens.ingressCredits
      : currentMetrics.ingressCredits,
    egressCredits: featureTokens?.egressCredits
      ? (currentMetrics.egressCredits || 0) + featureTokens.egressCredits
      : currentMetrics.egressCredits,
    lastUpdated: new Date(),
  };
}

// Initial state factory
export function createInitialConversationState(): ConversationState {
  return {
    messages: [],
    isComplete: false,
    totalMetrics: {},
    totalAggregatedMetrics: {
      ingressTokens: 0,
      egressTokens: 0,
      ingressBytes: 0,
      egressBytes: 0,
      totalCredits: 0,
      ingressCredits: 0,
      egressCredits: 0,
      balance: undefined,
      lastUpdated: new Date(),
    },
  };
}
